import { type ComponentProps, type PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useTranslation } from 'next-i18next';
import type { DTOMessageThreadInfo } from 'src/apis/dto/DTOMessageThreadInfo';
import CountryIconBase from 'src/components/CountryFlagIcon';
import { useDirectMessageNavigation, useDirectMessageProps } from 'src/components/DirectMessage/context';
import { useShowDMConfirmModal } from 'src/components/DirectMessage/DMConfirmModal';
import { defaultProfileImage } from 'src/components/ProfileImage';
import { useBlockAndLeaveMessageThreadMutation, useLeaveMessageThreadMutation } from 'src/hooks/useDirectMessageQuery';
import { useDMEventLog } from 'src/hooks/useDMEventLog';
import { formatTimestamp } from 'src/utils/date';
import { getMessageSummary } from 'src/utils/directMessage';
import RoomItemMoreMenu from './RoomItemMoreMenu';
import { Action, AvatarContainer, AvatarImage, Badge, Container, Content, HeaderRow, MessageText, OnlineDot, SwipeableContent, Time, UserName, SWIPE_BUTTON_WIDTH } from './styles';
import SwipeActions from './SwipeActions';
const CountryIcon = styled(CountryIconBase)`
  width: 16px;

  margin-block-start: 3px;
  margin-inline-start: -2px;
`;
interface Props extends Pick<ComponentProps<'li'>, 'className'> {
  messageThreadInfo: DTOMessageThreadInfo;
}
const DEFAULT_SWIPE_THRESHOLD = SWIPE_BUTTON_WIDTH * 2;
const RoomItem = ({
  messageThreadInfo,
  ...props
}: PropsWithChildren<Props>) => {
  const {
    t
  } = useTranslation();
  const {
    isRtl
  } = useTheme();
  const showDMConfirmModal = useShowDMConfirmModal();
  const {
    friendInfo: {
      smallProfileImageUrl,
      simpleName,
      location,
      friendId
    },
    dateLastMessageSent,
    lastMessageSeqNo = 0,
    messageSeqNoUserRead = 0,
    messageThreadId,
    online
  } = messageThreadInfo;
  const {
    mutate: leaveMessageThread
  } = useLeaveMessageThreadMutation();
  const {
    mutate: blockAndLeaveMessageThread
  } = useBlockAndLeaveMessageThreadMutation();
  const {
    openRoom,
    closeRoom
  } = useDirectMessageNavigation();
  const {
    messageThreadId: activeThreadId
  } = useDirectMessageProps();
  const isSelected = activeThreadId === messageThreadId;

  // 비동기 콜백에서 최신 activeThreadId를 참조하기 위한 ref.
  // isSelected는 렌더 시점에 클로저로 캡처되어, 비동기 작업 중 사용자가 다른 방을 열어도
  // 여전히 true로 남아있는 stale closure 문제를 방지한다.
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;
  const unreadCount = useMemo(() => Math.max(lastMessageSeqNo - messageSeqNoUserRead, 0), [lastMessageSeqNo, messageSeqNoUserRead]);
  const messageSummary = getMessageSummary(messageThreadInfo, t);
  const {
    logOpenSettings,
    logClickLeave,
    logBlockUser
  } = useDMEventLog();
  const wasButtonsVisible = useRef(false);
  const [{
    x
  }, api] = useSpring(() => ({
    x: 0,
    config: {
      tension: 300,
      friction: 30
    }
  }));
  const bind = useDrag(({
    active,
    movement: [mx],
    direction: [dx],
    memo
  }) => {
    const threshold = DEFAULT_SWIPE_THRESHOLD;
    const startX = memo ?? x.get();
    const currentX = startX + mx;
    const clampedX = isRtl ? Math.min(0, Math.max(currentX, -threshold)) : Math.max(0, Math.min(currentX, threshold));
    if (active) {
      api.start({
        x: clampedX,
        immediate: true
      });
    } else {
      const isOpeningDirection = isRtl ? dx < 0 : dx > 0;
      const absX = Math.abs(clampedX);
      const openThreshold = threshold / 3;
      const closeThreshold = threshold * 2 / 3;
      const willShowButtons = absX > (isOpeningDirection ? openThreshold : closeThreshold);
      api.start({
        x: willShowButtons ? isRtl ? -threshold : threshold : 0
      });
      if (willShowButtons && !wasButtonsVisible.current) {
        logOpenSettings({
          messageThreadId,
          page: 'main'
        });
      }
      wasButtonsVisible.current = willShowButtons;
    }
    return startX;
  }, {
    axis: 'x',
    filterTaps: true
  });
  const handleOpenRoom = () => {
    if (isRtl ? x.get() < 0 : x.get() > 0) {
      api.start({
        x: 0
      });
      wasButtonsVisible.current = false;
      return;
    }
    openRoom(messageThreadId);
  };
  const blockAndLeaveAction = () => {
    logClickLeave({
      messageThreadId,
      page: 'main',
      option: 'block_and_leave'
    });
    blockAndLeaveMessageThread({
      friendId
    }, {
      onSuccess: () => {
        logBlockUser({
          messageThreadId,
          page: 'main'
        });
        if (activeThreadIdRef.current === messageThreadId) {
          closeRoom();
        }
      }
    });
  };
  const leaveAction = () => {
    logClickLeave({
      messageThreadId,
      page: 'main',
      option: 'leave'
    });
    leaveMessageThread(messageThreadId, {
      onSuccess: () => {
        if (activeThreadIdRef.current === messageThreadId) {
          closeRoom();
        }
      }
    });
  };
  const handleBlockAndLeave = () => {
    api.start({
      x: 0
    });
    wasButtonsVisible.current = false;
    showDMConfirmModal('blockAndLeave', blockAndLeaveAction);
  };
  const handleLeave = () => {
    api.start({
      x: 0
    });
    wasButtonsVisible.current = false;
    showDMConfirmModal('leave', leaveAction);
  };
  const progress = x.to(val => Math.min(Math.abs(val) / DEFAULT_SWIPE_THRESHOLD, 1));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return <Container {...props} isSelected={isSelected} isMenuOpen={isMenuOpen} data-sentry-element="Container" data-sentry-component="RoomItem" data-sentry-source-file="Item.tsx">
      <SwipeActions progress={progress} onBlockAndLeave={handleBlockAndLeave} onLeave={handleLeave} data-sentry-element="SwipeActions" data-sentry-source-file="Item.tsx" />
      <SwipeableContent {...bind()} style={{
      x
    }} data-sentry-element="SwipeableContent" data-sentry-source-file="Item.tsx">
        <Action type='button' onClick={handleOpenRoom} data-sentry-element="Action" data-sentry-source-file="Item.tsx">
          <AvatarContainer data-sentry-element="AvatarContainer" data-sentry-source-file="Item.tsx">
            <AvatarImage src={smallProfileImageUrl ?? defaultProfileImage} alt={simpleName} draggable={false} data-sentry-element="AvatarImage" data-sentry-source-file="Item.tsx" />
            {online && <OnlineDot />}
          </AvatarContainer>
          <Content data-sentry-element="Content" data-sentry-source-file="Item.tsx">
            <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="Item.tsx">
              <UserName data-sentry-element="UserName" data-sentry-source-file="Item.tsx">{simpleName}</UserName>
              <CountryIcon countryCode={location?.countryCode} data-sentry-element="CountryIcon" data-sentry-source-file="Item.tsx" />
              {dateLastMessageSent && <Time>{formatTimestamp(dateLastMessageSent)}</Time>}
            </HeaderRow>
            <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="Item.tsx">
              <MessageText data-sentry-element="MessageText" data-sentry-source-file="Item.tsx">{messageSummary}</MessageText>
              {unreadCount > 0 && <Badge type='number' size='big' value={unreadCount} />}
            </HeaderRow>
          </Content>
        </Action>
      </SwipeableContent>
      <RoomItemMoreMenu messageThreadId={messageThreadId} onBlockAndLeave={handleBlockAndLeave} onLeave={handleLeave} onOpenChange={setIsMenuOpen} data-sentry-element="RoomItemMoreMenu" data-sentry-source-file="Item.tsx" />
    </Container>;
};
export default RoomItem;